import React from 'react';
import {
  Car, Utensils, Repeat, ReceiptText, Coffee, Wifi, Phone, Fuel,
  ShoppingCart, Home, Zap, HeartPulse, Gamepad2, Plane, Gift,
  Apple, Shirt, Baby, GraduationCap, Wrench, Building2, Banknote, Cigarette, Dog, Dumbbell,
} from 'lucide-react';

export const CAT_ICONS = {
  car: Car, utensils: Utensils, repeat: Repeat, receipt: ReceiptText,
  coffee: Coffee, wifi: Wifi, phone: Phone, fuel: Fuel, cart: ShoppingCart,
  home: Home, zap: Zap, health: HeartPulse, game: Gamepad2, plane: Plane, gift: Gift,
  fruit: Apple, clothes: Shirt, baby: Baby, school: GraduationCap, tools: Wrench,
  office: Building2, cash: Banknote, smoke: Cigarette, pet: Dog, gym: Dumbbell,
};

// Distinct colour per icon family so each expense type is instantly recognisable.
export const CAT_COLORS = {
  car: '#5b8cff', fuel: '#ff8a3d', utensils: '#ff5d7e', fruit: '#39d98a',
  coffee: '#b07a4b', cart: '#ffcf3d', wifi: '#3dd6ff', phone: '#7c6bff',
  receipt: '#ff9f43', home: '#4dd0e1', zap: '#ffd93d', health: '#ff5b6e',
  game: '#a66bff', plane: '#3d9bff', gift: '#ff6fb5', clothes: '#c084fc',
  baby: '#ffb3c1', school: '#4db8ff', tools: '#8d99ae', office: '#9d7bff',
  cash: '#39d98a', smoke: '#9e9e9e', pet: '#ff9770', gym: '#ff6b6b',
  repeat: '#5eead4',
};

// Currency symbols — shows the RIGHT sign for the amount the user actually spoke.
export const CUR_SYMBOLS = { USD: '$', ILS: '₪', JOD: 'JD', EUR: '€', GBP: '£', AED: 'د.إ' };
export const curSym = (c) => CUR_SYMBOLS[(c || 'USD').toUpperCase()] || (c || '');

// Keyword → icon resolver: maps a free-text category (Arabic or English) to an
// icon so voice-added expenses ("مصروف بنزين", "فواكه") get the right glyph.
const KEYWORD_ICON = [
  [['بنزين', 'وقود', 'محروقات', 'gas', 'fuel', 'petrol', 'ديزل'], 'fuel'],
  [['فواكه', 'فاكهة', 'خضار', 'خضروات', 'fruit', 'vegetable'], 'fruit'],
  [['اكل', 'أكل', 'طعام', 'مطعم', 'وجبة', 'غدا', 'عشا', 'فطور', 'food', 'lunch', 'dinner', 'restaurant', 'meal'], 'utensils'],
  [['قهوة', 'كوفي', 'coffee', 'cafe', 'كافيه'], 'coffee'],
  [['سوبر', 'ماركت', 'بقالة', 'تسوق', 'مقاضي', 'grocery', 'market', 'shopping'], 'cart'],
  [['مواصلات', 'تكسي', 'سيارة', 'اوبر', 'taxi', 'uber', 'car', 'transport', 'باص'], 'car'],
  [['نت', 'انترنت', 'wifi', 'internet', 'اشتراك نت'], 'wifi'],
  [['موبايل', 'هاتف', 'تلفون', 'رصيد', 'phone', 'mobile'], 'phone'],
  [['فاتورة', 'فواتير', 'كهربا', 'كهرباء', 'ماء', 'مي', 'bill', 'electric', 'water'], 'receipt'],
  [['ايجار', 'أجار', 'اجار', 'بيت', 'سكن', 'rent', 'home', 'house'], 'home'],
  [['صحة', 'دكتور', 'دواء', 'صيدلية', 'مستشفى', 'health', 'doctor', 'pharmacy', 'medicine'], 'health'],
  [['ملابس', 'لبس', 'clothes', 'clothing'], 'clothes'],
  [['اطفال', 'أطفال', 'baby', 'kids'], 'baby'],
  [['مدرسة', 'جامعة', 'تعليم', 'school', 'university', 'education'], 'school'],
  [['صيانة', 'تصليح', 'ادوات', 'tools', 'repair', 'maintenance'], 'tools'],
  [['مكتب', 'شغل', 'office', 'work'], 'office'],
  [['سفر', 'طيارة', 'رحلة', 'travel', 'flight', 'trip'], 'plane'],
  [['هدية', 'هدايا', 'gift'], 'gift'],
  [['اشتراك', 'subscription', 'netflix', 'spotify'], 'repeat'],
  [['رياضة', 'جيم', 'نادي', 'gym', 'fitness'], 'gym'],
  [['دخان', 'سجائر', 'cigarette', 'smoke'], 'smoke'],
];

export const iconForCategory = (name) => {
  const n = (name || '').toString().toLowerCase().replace(/[إأآ]/g, 'ا');
  for (const [kws, icon] of KEYWORD_ICON) {
    if (kws.some((k) => n.includes(k.toLowerCase().replace(/[إأآ]/g, 'ا')))) return icon;
  }
  return null;
};

// Resolve visual for a transaction (explicit icon wins, else keyword match, else fallback)
export const categoryVisual = ({ icon, category } = {}) => {
  const key = (icon && CAT_ICONS[icon]) ? icon : (iconForCategory(category) || 'zap');
  return { icon: key, color: CAT_COLORS[key] || 'var(--pk-cyan)' };
};

export const CatIcon = ({ icon, className, style, ...rest }) => {
  const I = CAT_ICONS[icon] || Zap;
  return <I className={className} style={style} strokeWidth={1.7} {...rest} />;
};
